import { Image, Page, Text, View } from '@react-pdf/renderer'
import { Empty, Spin } from 'antd'
import { SectionHeader } from 'pages/reporting/reports/externalVisual/components/VisualInspectionSectionHeader'
import { useVisualReportOps } from 'pages/reporting/reports/externalVisual/VisualReportProvider'
import GeckoLogo from 'pages/reporting/shared/pdfGeneration/components/shared/GeckoLogo'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { PDFStyles } from 'pages/reporting/shared/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { useMemo } from 'react'

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData, visualInspectionImages } = useVisualReportOps()

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  return (
    <Page size='A4' style={PDFStyles.page} id='reference-images' wrap={false}>
      {/* Hidden text for page tracking */}
      <Text
        render={({ pageNumber }) => {
          onRender({ id: 'reference-images', pageNumber })
          return ''
        }}
      />

      <View style={PDFStyles.content}>
        {/* Header with Gecko logo */}
        <View style={PDFStyles.coverHeader}>
          <GeckoLogo />
          <View style={PDFStyles.coverHeaderContent}>
            <Text style={PDFStyles.coverHeaderText}>
              Checklist Inspection Report
            </Text>
          </View>
        </View>

        {/* Section Header */}
        <SectionHeader title='Reference Images' />

        {/* Content */}
        <View style={{ marginTop: 10 }}>
          {visualInspectionImages.images.length > 0 ? (
            visualInspectionImages.images.map((image) => {
              if (!image.signedUrl) return null

              return (
                <View key={image.report.id} style={{ marginBottom: 30 }}>
                  {/* Image Header */}
                  <View style={{ marginBottom: 10 }}>
                    <Text style={{ fontSize: 11, fontWeight: 700 }}>
                      ID: ({image.id})
                    </Text>
                    <Text style={{ fontSize: 10, marginTop: 5 }}>
                      Description: {image.description}
                    </Text>
                  </View>

                  {/* Image */}
                  <View
                    style={{
                      border: '1 solid #E5E7EB',
                      padding: 10,
                      backgroundColor: '#F9FAFB',
                    }}>
                    <Image
                      src={image.signedUrl}
                      style={{
                        width: '100%',
                        maxHeight: 400,
                        objectFit: 'contain',
                      }}
                    />
                  </View>
                </View>
              )
            })
          ) : (
            <Text style={{ fontSize: 10 }}>No images to display.</Text>
          )}
        </View>
      </View>

      <PageFooter
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={isPrelim}
      />
    </Page>
  )
}

const RenderReact: React.FC = () => {
  const { visualInspectionImages } = useVisualReportOps()

  if (visualInspectionImages.isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size='large' />
        <p style={{ marginTop: 16 }}>Loading reference images...</p>
      </div>
    )
  }

  if (visualInspectionImages.images.length === 0) {
    return (
      <Empty
        description='No reference images found for this visual inspection'
        style={{ padding: 50 }}
      />
    )
  }

  return (
    <div style={{ padding: 24 }}>
      <h2>Reference Images</h2>
      <p style={{ marginBottom: 24 }}>
        The following images will be included in the report:
      </p>

      {visualInspectionImages.images.map((image) => (
        <div
          key={image.report.id}
          style={{
            marginBottom: 32,
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            padding: 16,
          }}>
          <h3>ID: ({image.id})</h3>
          <p>
            <strong>Description:</strong> {image.description}
          </p>
          {image.signedUrl && (
            <div
              style={{
                marginTop: 16,
                backgroundColor: '#f5f5f5',
                padding: 16,
                borderRadius: 4,
                textAlign: 'center',
              }}>
              <img
                src={image.signedUrl}
                alt={image.description}
                style={{
                  maxWidth: '100%',
                  maxHeight: 400,
                  objectFit: 'contain',
                }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export const ReferenceImagesSection: ReportSection = {
  RenderReact,
  required: false,
  RenderPDF,
  id: 'referenceImages',
  description: 'Images from the visual inspection',
  title: 'Reference Images',
  requiredFields: [], // No form fields required
}

export const ReferenceImagesSectionPDF = RenderPDF
